import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import AddressAutocomplete from '../../../shared/components/Forms/AddressAutocomplete.component';
import { ParsedAddress } from '../../../shared/components/Forms/parse-google-address.util';
import {
  ADDRESS_COMMENT_LABEL,
  PLACE_SELECTION_HINT,
  ZIP_CODE_FORMAT_ERROR,
} from '../../../shared/components/Forms/address-form.constants';
import { DeliveryPointDetailsAddress } from '../../types/delivery-point-details.type';
import { BOUNDED_TEXT_ERROR, isBoundedTextInvalid, isZipCodeInvalid } from '../../utils/delivery-point-form-rules.util';

type AddressField = keyof DeliveryPointDetailsAddress;

type Props = {
  idPrefix: string;
  address: DeliveryPointDetailsAddress;
  addressLabel: string;
  isPlaceSelected: boolean;
  required?: boolean;
  onAddressLabelChange: (value: string) => void;
  onAddressSelected: (place: ParsedAddress) => void;
  onAddressFieldChange: (change: { field: AddressField; value: string }) => void;
};

const useStyles = makeStyles({
  cityRow: {
    display: 'flex',
    gap: '1rem',

    '& > div:first-child': {
      width: '35%',
    },
    '& > div:last-child': {
      width: '65%',
    },
  },
});

export function DeliveryPointAddressFields({
  idPrefix,
  address,
  addressLabel,
  isPlaceSelected,
  required = false,
  onAddressLabelChange,
  onAddressSelected,
  onAddressFieldChange,
}: Props) {
  const classes = useStyles();

  const isPlacePending = addressLabel.length > 0 && !isPlaceSelected;
  const isZipCodeFieldInvalid = isZipCodeInvalid(address.zipCode);
  const isCityFieldInvalid = isBoundedTextInvalid(address.city);
  const isInformationsFieldInvalid = isBoundedTextInvalid(address.informations ?? '');
  const isCommentFieldInvalid = isBoundedTextInvalid(address.comment ?? '');

  const renderFreeTextField = (field: 'informations' | 'comment', label: string, isInvalid: boolean) => (
    <TextField
      id={`${idPrefix}-${field}`}
      label={label}
      type="text"
      size="small"
      variant="outlined"
      fullWidth
      error={isInvalid}
      helperText={isInvalid ? BOUNDED_TEXT_ERROR : undefined}
      value={address[field] ?? ''}
      onChange={(event) => onAddressFieldChange({ field, value: event.target.value })}
    />
  );

  return (
    <>
      <AddressAutocomplete
        id={`${idPrefix}-address-search`}
        label="Adresse"
        size="small"
        value={addressLabel}
        onChange={onAddressLabelChange}
        onAddressSelected={onAddressSelected}
        helperText={isPlacePending ? PLACE_SELECTION_HINT : undefined}
      />
      <TextField
        id={`${idPrefix}-street`}
        label="Rue"
        type="text"
        size="small"
        variant="outlined"
        fullWidth
        required={required}
        disabled={!isPlaceSelected}
        value={address.street}
        onChange={(event) => onAddressFieldChange({ field: 'street', value: event.target.value })}
      />
      <div className={classes.cityRow}>
        <TextField
          id={`${idPrefix}-zip-code`}
          label="Code postal"
          type="text"
          size="small"
          variant="outlined"
          required={required}
          disabled={!isPlaceSelected}
          error={isZipCodeFieldInvalid}
          helperText={isZipCodeFieldInvalid ? ZIP_CODE_FORMAT_ERROR : undefined}
          inputProps={{ inputMode: 'numeric', maxLength: 5 }}
          value={address.zipCode}
          onChange={(event) => onAddressFieldChange({ field: 'zipCode', value: event.target.value })}
        />
        <TextField
          id={`${idPrefix}-city`}
          label="Ville"
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          required={required}
          disabled={!isPlaceSelected}
          error={isCityFieldInvalid}
          helperText={isCityFieldInvalid ? BOUNDED_TEXT_ERROR : undefined}
          value={address.city}
          onChange={(event) => onAddressFieldChange({ field: 'city', value: event.target.value })}
        />
      </div>
      <TextField
        id={`${idPrefix}-country`}
        label="Pays"
        type="text"
        size="small"
        variant="outlined"
        fullWidth
        disabled={!isPlaceSelected}
        value={address.country ?? ''}
        onChange={(event) => onAddressFieldChange({ field: 'country', value: event.target.value })}
      />
      {renderFreeTextField('informations', 'Informations', isInformationsFieldInvalid)}
      {renderFreeTextField('comment', ADDRESS_COMMENT_LABEL, isCommentFieldInvalid)}
    </>
  );
}
